import { useMutation } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

import { salesCategoriesCreate } from "~/api-refs";
import {
  EMPTY_SALES_CATEGORY_FORM_VALUES,
  SalesCategoryFormModal,
  type SalesCategoryFormValues,
} from "~/components/settings/sales-category-form-modal";
import { convexClient } from "~/integrations/convex/provider";
import { logger } from "~/lib/logger";
import { ProductEvents } from "~/lib/posthog";

export const Route = createFileRoute(
  "/_authenticated/settings/sales-categories/new"
)({
  component: CreateSalesCategoryModal,
});

function CreateSalesCategoryModal() {
  const navigate = useNavigate();

  const form = useForm<SalesCategoryFormValues>({
    defaultValues: EMPTY_SALES_CATEGORY_FORM_VALUES,
  });

  const createSalesCategoryMutation = useMutation({
    mutationFn: (data: { name: string }) => {
      return convexClient.mutation(salesCategoriesCreate, data);
    },
    onSuccess: (result) => {
      if (result) {
        ProductEvents.salesCategoryCreated(result);
      }
    },
  });

  const handleClose = () => {
    void navigate({ to: "/settings/sales-categories" });
  };

  const handleSubmit = async (values: SalesCategoryFormValues) => {
    if (createSalesCategoryMutation.isPending) {
      return;
    }

    try {
      await createSalesCategoryMutation.mutateAsync(values);
      toast.success("Sales Category added successfully");
      handleClose();
    } catch (error) {
      toast.error("Error adding Sales Category");
      logger.exception("Failed to create sales category", error);
    }
  };

  return (
    <SalesCategoryFormModal
      description="Create a new sales category for your items."
      form={form}
      isSubmitting={createSalesCategoryMutation.isPending}
      onClose={handleClose}
      onSubmit={handleSubmit}
      submitLabel="Add"
      title="Add Sales Category"
    />
  );
}
